const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const TZ = 'Asia/Jakarta'
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/

function jakartaParts(d: Date): Record<string, string> {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(d)
  return Object.fromEntries(parts.map((p) => [p.type, p.value]))
}

/** "01 Oct 2026". Plain YYYY-MM-DD strings are formatted as-is (no timezone shift). */
export function fmtDate(value?: string | null): string {
  if (!value) return '—'
  const m = DATE_ONLY.exec(value)
  if (m) return `${m[3]} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  const p = jakartaParts(d)
  return `${p.day} ${MONTHS[Number(p.month) - 1]} ${p.year}`
}

/** "Wed, 01 Oct 2026" readout for a YYYY-MM-DD value, independent of the browser locale. */
export function dateReadout(ymd: string): string {
  const m = DATE_ONLY.exec(ymd)
  if (!m) return ''
  const wd = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' })
  return `${wd}, ${fmtDate(ymd)}`
}

/** "01 Oct 2026, 14:54 WIB" (Asia/Jakarta). */
export function fmtDateTime(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const p = jakartaParts(d)
  return `${p.day} ${MONTHS[Number(p.month) - 1]} ${p.year}, ${p.hour}:${p.minute} WIB`
}

/** YYYY-MM-DD in Asia/Jakarta. */
export function jakartaYmd(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Jakarta',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now)
}

/** Shift a calendar date by whole days. `2026-10-01` minus 6 is `2026-09-25`. */
export function shiftYmd(ymd: string, days: number): string {
  const m = DATE_ONLY.exec(ymd)
  if (!m) return ymd
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days))
  const y = d.getUTCFullYear()
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0')
  const da = String(d.getUTCDate()).padStart(2, '0')
  return `${y}-${mo}-${da}`
}

/** Last 7 Jakarta days ending today, inclusive. Always inside a 31-day window. */
export function last7Days(today = jakartaYmd()): { from: string; to: string } {
  return { from: shiftYmd(today, -6), to: today }
}

export const fmtTime = fmtDateTime

/** id-ID grouping, e.g. 8.125.000,00. Returns an em dash for empty or non-numeric input. */
export function fmtNum(value: unknown, maxFractionDigits = 2): string {
  if (value == null || value === '') return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return new Intl.NumberFormat('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: maxFractionDigits }).format(n)
}

/** Currency prefix + id-ID grouping. IDR has no decimals; other currencies have two. */
export function fmtMoney(value: unknown, currency = ''): string {
  if (value == null || value === '') return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  const digits = currency.toUpperCase() === 'IDR' ? 0 : 2
  const num = new Intl.NumberFormat('id-ID', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n)
  return currency ? `${currency} ${num}` : num
}

export function dash(value: unknown): string {
  if (value == null || value === '') return '—'
  return String(value)
}

export function asRecord(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }
  return {}
}

export function mtAccount(login?: string | number | null): string {
  if (login == null || login === '') return '—'
  return `MT5 Account (${login})`
}

export function splitIb(raw?: string | null): { ib: string; team: string } {
  if (!raw) return { ib: '—', team: '—' }
  const i = raw.indexOf('/')
  if (i < 0) return { ib: raw, team: '—' }
  return { ib: raw.slice(0, i) || '—', team: raw.slice(i + 1) || '—' }
}

export function downloadCsv(filename: string, headers: string[], rows: string[][]): void {
  const cell = (v: string) => `"${v.replaceAll('"', '""')}"`
  const body = [headers, ...rows].map((r) => r.map((c) => cell(c ?? '')).join(',')).join('\n')
  const blob = new Blob([body], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
